'use client';

import type { i18n, TFunction } from 'i18next';
import { COMPANY_COUNTRIES } from '@/lib/countries';
import type {
  CompanyBasicResponse,
  CompanyContactResponse,
  CompanyGesellschafterResponse,
  CompanyHistory,
  CompanyLegalStatus,
  CompanyNetworkResponse,
  InsolvencyRecordItem,
} from '@/lib/backend-api';
import { companyCountryCode } from '@/lib/company-utils';
import type { TopEmployerRankLink } from '@/lib/seo-lists/types';
import type { useSession } from '@/hooks/use-session';
import { Container } from '@/components/common/container';
import { formatRegisterNumber } from './companyDetailUtils';
import { MobileActionBar } from './MobileActionBar';
import { RegisterSourceNote } from './RegisterSourceNote';
import { FINANCIAL_DOCS_ANCHOR, REGISTER_DOCS_ANCHOR } from './companyDetailLayout';
import { CompanyDetailMainColumn } from './CompanyDetailMainColumn';
import { CompanyDetailSidebarColumn } from './CompanyDetailSidebarColumn';
import type {
  CompanyDetailEffectiveData,
  CompanyDetailFinancialViews,
} from './useCompanyDetailDerivedData';

interface CompanyDetailBodyProps {
  euId: string;
  ssrNetworkData: CompanyNetworkResponse | null | undefined;
  topEmployerRank: TopEmployerRankLink | null | undefined;
  basicData: CompanyBasicResponse | null;
  contactData: CompanyContactResponse | null;
  gesellschafterData: CompanyGesellschafterResponse | null;
  companyHistory: CompanyHistory | null;
  session: ReturnType<typeof useSession>['data'];
  loadingBackground: boolean;
  loadingRegister: boolean;
  loadingGesellschafter: boolean;
  networkReloadToken: number;
  insolvencyRecords: InsolvencyRecordItem[];
  legalStatus: CompanyLegalStatus;
  isAdminUser: boolean;
  isProOrAdmin: boolean;
  t: TFunction;
  i18n: i18n;
  /** From useCompanyDetailEffectiveData / useCompanyDetailFinancialViews. */
  effective: CompanyDetailEffectiveData;
  financial: CompanyDetailFinancialViews;
}

/**
 * CompanyDetailPage's loaded render inside its print wrapper: the two
 * columns, the register source note and the mobile action bar.
 *
 * ## CLS (Cumulative Layout Shift) Prevention Rules
 *
 * The SSR page must produce a stable layout for guests. Logged-in users get
 * progressive updates via client-side fetches, but the initial frame must not
 * jump. Follow these rules when adding or modifying sections:
 *
 * 1. **Parent conditions must match child thresholds.**
 *    If a chart component returns null when `data.length < N`, the parent
 *    conditional must use the SAME threshold. Otherwise a loading skeleton
 *    renders for the chunk, then the real component returns null → shift.
 *    Example: a chart that needs >= 50 values, so the parent checks >= 50.
 *
 * 2. **Gated sections must not flash a skeleton while permissions resolve**
 *    (session + guest-permission fetch): hidden gates render null during
 *    loading, visible ones render their children.
 *
 * 3. **Dynamic imports (`next/dynamic`) use `ssr: false` for recharts.**
 *    Recharts requires DOM measurement and can't SSR. The loading skeleton
 *    MUST match the expected rendered height. If the chart may return null
 *    based on data, tighten the parent condition (rule 1) so the skeleton
 *    never appears for cases that would produce null.
 *
 * 4. **Map containers need fixed height + background.**
 *    Leaflet is client-only. The `h-[200px] bg-muted/30` ensures the
 *    container occupies stable space before tiles load.
 *
 * 5. **Never conditionally render a section based on async-only data.**
 *    If data comes from a client fetch (contactData, financialsData), the
 *    section must either always render (with an empty/fallback state) or
 *    derive its condition from SSR-available basicData via effectiveXData.
 */
export function CompanyDetailBody({
  euId, ssrNetworkData, topEmployerRank, basicData, contactData,
  gesellschafterData, companyHistory, session, loadingBackground,
  loadingRegister, loadingGesellschafter, networkReloadToken,
  insolvencyRecords, legalStatus, isAdminUser, isProOrAdmin, t, i18n,
  effective, financial,
}: CompanyDetailBodyProps) {
  const {
    effectiveRegisterData, registerDocumentsSource, effectiveFinancialsData,
    hasGesellschafterliste, gesellschafterItems, heroData,
  } = effective;
  const {
    statistics, aktivaData, passivaData, guvData, aiGuvData, mergedGuvData,
    hasCostBreakdown, availableFinancialYears, revenueHistory,
    hasFinancialDocs, validationErrors,
  } = financial;

  const country = companyCountryCode(basicData?.register_type, basicData?.eu_id);
  // German Handelsregister documents (card + mobile action) exist for DE only.
  const hasRegisterDocs = COMPANY_COUNTRIES[country].germanRegisterDocuments;
  // Swiss register data is credited by the page-level RegisterSourceNote
  // instead; DE credits the Handelsregister, AT the Firmenbuch (per card).
  const showRegisterAttribution = !!basicData && country !== 'CH';
  const attributionRegisterId = formatRegisterNumber(
    basicData?.register_type,
    basicData?.register_id,
  );

  return (
    <>
      {/* pb-24 below lg keeps the last card clear of the fixed action bar. */}
      <Container className="flex flex-col pt-0 pb-24 lg:pb-8 px-0 lg:px-0">
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] overflow-hidden border-b border-border">
          {/* Left Column - Top (Profile, Managers, Register, Subsidiaries).
              `contents` below lg dissolves this wrapper so its cards become
              direct grid items and MOBILE_ORDER can interleave them with the
              sidebar's; `lg:flex` restores the desktop column. */}
          <CompanyDetailMainColumn
            basicData={basicData}
            effectiveRegisterData={effectiveRegisterData}
            effectiveFinancialsData={effectiveFinancialsData}
            contactData={contactData}
            heroData={heroData}
            statistics={statistics}
            loadingBackground={loadingBackground}
            euId={euId}
            ssrNetworkData={ssrNetworkData}
            topEmployerRank={topEmployerRank}
            networkReloadToken={networkReloadToken}
            insolvencyRecords={insolvencyRecords}
            legalStatus={legalStatus}
            hasGesellschafterliste={hasGesellschafterliste}
            country={country}
            gesellschafterItems={gesellschafterItems}
            companyHistory={companyHistory}
            session={session}
            showRegisterAttribution={showRegisterAttribution}
            attributionRegisterId={attributionRegisterId}
            isAdminUser={isAdminUser}
            aktivaData={aktivaData}
            passivaData={passivaData}
            guvData={guvData}
            aiGuvData={aiGuvData}
            t={t}
            validationErrors={validationErrors}
            availableFinancialYears={availableFinancialYears}
          />

          {/* Right Column - Sidebar (Financials, Register, Kontakt, Costs).
              Same `contents` trick as the left column — see MOBILE_ORDER. */}
          <CompanyDetailSidebarColumn
            revenueHistory={revenueHistory}
            mergedGuvData={mergedGuvData}
            heroData={heroData}
            hasRegisterDocs={hasRegisterDocs}
            effectiveRegisterData={effectiveRegisterData}
            registerDocumentsSource={registerDocumentsSource}
            gesellschafterData={gesellschafterData}
            basicData={basicData}
            hasGesellschafterliste={hasGesellschafterliste}
            euId={euId}
            session={session}
            loadingRegister={loadingRegister}
            loadingGesellschafter={loadingGesellschafter}
            showRegisterAttribution={showRegisterAttribution}
            attributionRegisterId={attributionRegisterId}
            contactData={contactData}
            effectiveFinancialsData={effectiveFinancialsData}
            isProOrAdmin={isProOrAdmin}
            hasCostBreakdown={hasCostBreakdown}
            i18n={i18n}
          />
        </div>
        {/* Register data attribution and modification notice. */}
        {basicData && (
          <RegisterSourceNote
            country={country}
            locationAttribution={basicData.location?.attribution}
          />
        )}
      </Container>
      {/* The register-document action is available for German companies. */}
      <MobileActionBar
        euId={basicData?.eu_id ?? euId}
        isAuthenticated={!!session}
        financialDocsAnchor={FINANCIAL_DOCS_ANCHOR}
        registerDocsAnchor={REGISTER_DOCS_ANCHOR}
        hasFinancialDocs={hasFinancialDocs}
        hasRegisterDocs={hasRegisterDocs}
      />
    </>
  );
}
